import { Circle, GoogleMap, Marker, OverlayView, Polyline, useLoadScript } from "@react-google-maps/api";
import cx from "classnames";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { GOOGLE_MAPS_LOADER_OPTIONS } from "@/lib/googleMaps";
import { Spinner } from "../";

/** Imperative handle for external zoom controls (e.g. the booking sheet's +/- buttons). */
export interface MapHandle {
  zoomIn: () => void;
  zoomOut: () => void;
}

export interface MapProps {
  circleRadius?: number;
  center?: Coordinates | null;
  marker?: Coordinates | null;
  zoom?: number;
  className?: string;
  /**
   * Renders a draggable location pin anchored to `marker` (stays on the geographic point while
   * zooming/panning) and fires `onPinChange` with the new position on drag end.
   */
  pinMode?: boolean;
  onPinChange?: (coordinates: Coordinates) => void;
  /** Static booking markers — `dot` for pickup, `pin` (teardrop) for stops/destination. */
  markers?: { position: Coordinates; variant?: "dot" | "pin" }[];
  /** Decoded route path drawn as a polyline through the stops (booking route preview). */
  routePath?: Coordinates[];
  /**
   * A label pinned to a geographic point on the map — used for the booking route's distance/time
   * estimate. Anchored rather than absolutely positioned so it stays on the route through pan and
   * zoom, the way the markers and polyline do.
   */
  routeBadge?: { label: React.ReactNode; position: Coordinates } | null;
  /** Fit the viewport to `markers` + `routePath` (ignored in pinMode). */
  fitToMarkers?: boolean;
  /** Padding (px) applied to `fitToMarkers` — pass directional padding to keep markers clear of an
   * overlaying panel (e.g. the booking Address panel). Defaults to 64 on all sides. */
  fitPadding?: number | google.maps.Padding;
}

export type Coordinates = {
  lat: number;
  lng: number;
};

// Sydney, New South Wales, Australia
const sydneyCenter: Coordinates = {
  lat: -33.865143,
  lng: 151.2099,
};

/**
 * Can Google plot this point?
 *
 * Google throws a hard InvalidValueError — not a warning — for a non-finite coordinate, from
 * setCenter, setPosition and bounds.extend alike. Those calls sit in render and effects, so the throw
 * escapes to the nearest error boundary and blanks the page instead of degrading the map.
 *
 * Reachable from real data: a booking whose address was never validated has no lat/lng at all, and
 * the booking sheet renders one marker per location regardless.
 */
const isPlottable = (p?: Coordinates | null): p is Coordinates =>
  !!p && Number.isFinite(p.lat) && Number.isFinite(p.lng);

// Brand map styling, ported from the mobile app (app-customer `defaultMapStyle`) for a consistent look.
const MAP_STYLES: google.maps.MapTypeStyle[] = [
  { elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#f5f5f5" }] },
  { featureType: "administrative.land_parcel", elementType: "labels.text.fill", stylers: [{ color: "#bdbdbd" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#eeeeee" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#ceead6" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#278844" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.arterial", elementType: "labels.text.fill", stylers: [{ color: "#2a4592" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#dadada" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { featureType: "road.local", elementType: "labels.text.fill", stylers: [{ color: "#2a4592" }] },
  { featureType: "transit.line", elementType: "geometry", stylers: [{ color: "#e5e5e5" }] },
  { featureType: "transit.station", elementType: "geometry", stylers: [{ color: "#eeeeee" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#b7e4f4" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#9e9e9e" }] },
];

// Vivid Purple Dark (tailwind `vivid.purple-dark`) — the brand map color for the location pin
// and the booking route/markers, per Figma.
const PIN_COLOR = "#6941C6";
// Location pin (matches Figma `iconsax-location`): teardrop with a white centre, tip at bottom.
const PIN_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="46" viewBox="0 0 36 46"><path d="M18 0.5C13.2571 0.5 8.70582 2.37146 5.3353 5.70828C1.96477 9.0451 0.0474287 13.5774 0 18.32C0 30.65 15.8625 44.375 16.5375 44.96C16.945 45.3086 17.4637 45.5 18 45.5C18.5363 45.5 19.055 45.3086 19.4625 44.96C20.25 44.375 36 30.65 36 18.32C35.9526 13.5774 34.0352 9.0451 30.6647 5.70828C27.2942 2.37146 22.7429 0.5 18 0.5Z" fill="${PIN_COLOR}"/><circle cx="18" cy="18" r="6.5" fill="white"/></svg>`;
const PIN_DATA_URI = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(PIN_SVG)}`;
// Pickup marker: a solid Vivid-Purple-Dark dot with a white ring (Figma node 4002-23881).
const DOT_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20"><circle cx="10" cy="10" r="8" fill="${PIN_COLOR}" stroke="white" stroke-width="2"/></svg>`;
const DOT_DATA_URI = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(DOT_SVG)}`;
// Vivid Purple (tailwind `vivid.purple`) — the booking route/directions line, per Figma node 4002-23880.
const ROUTE_COLOR = "#9E77ED";

export const Map = forwardRef<MapHandle, MapProps>(function Map(
  { circleRadius, center, marker, zoom, className, pinMode, onPinChange, markers, routePath, routeBadge, fitToMarkers, fitPadding = 64 },
  ref
) {
  const { isLoaded } = useLoadScript(GOOGLE_MAPS_LOADER_OPTIONS);
  const [currentPosition, setCurrentPosition] = useState<Coordinates | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  // Falls through to the user's position, then Sydney, rather than passing a point Google will reject.
  const centerPosition = isPlottable(center) ? center : isPlottable(currentPosition) ? currentPosition : sydneyCenter;

  // Expose zoom to external controls (the map hides Google's default UI via disableDefaultUI).
  useImperativeHandle(ref, () => ({
    zoomIn: () => {
      const z = mapRef.current?.getZoom();
      if (z != null) mapRef.current?.setZoom(z + 1);
    },
    zoomOut: () => {
      const z = mapRef.current?.getZoom();
      if (z != null) mapRef.current?.setZoom(z - 1);
    },
  }));

  // Resolve the user's location to bias centering — but never block the map render on it.
  // getCurrentPosition can hang indefinitely while a permission prompt is unanswered, so the
  // map renders immediately (center/markers/Sydney default) and recentres if/when this resolves.
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((position) =>
      setCurrentPosition({ lat: position.coords.latitude, lng: position.coords.longitude })
    );
  }, [setCurrentPosition]);

  // In pinMode, smoothly pan to centre changes (a controlled `center` prop jumps instantly).
  useEffect(() => {
    if (pinMode && mapRef.current && isPlottable(center)) mapRef.current.panTo(center);
  }, [center, pinMode]);

  // Fit the viewport to plotted markers + route (booking preview). Skipped in pinMode.
  useEffect(() => {
    if (pinMode || !fitToMarkers || !mapRef.current || !window.google?.maps) return;
    /*
     * Only points we can actually plot. Google throws a hard InvalidValueError out of extend() and
     * setCenter() for a non-finite coordinate, and because this runs in an effect the throw escapes to
     * the nearest error boundary and blanks the whole page rather than degrading the map.
     *
     * A booking whose address was never validated has no lat/lng at all, so this is reachable from
     * real data, not just from a caller's mistake. Dropping the point loses the fit for that one
     * marker; passing it through loses the screen.
     */
    const points = [...(markers?.map((m) => m.position) ?? []), ...(routePath ?? [])].filter(isPlottable);
    if (points.length === 0) return;
    if (points.length === 1) {
      mapRef.current.setCenter(points[0]);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend(p));
    mapRef.current.fitBounds(bounds, fitPadding);
  }, [markers, routePath, fitToMarkers, pinMode, fitPadding]);

  if (!isLoaded) {
    return (
      <div className="flex w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  // Custom pin icon, anchored at the teardrop tip (built once the Maps API is available).
  const pinIcon: google.maps.Icon = {
    url: PIN_DATA_URI,
    scaledSize: new google.maps.Size(34, 44),
    anchor: new google.maps.Point(17, 44),
  };
  // Pickup dot, centre-anchored.
  const dotIcon: google.maps.Icon = {
    url: DOT_DATA_URI,
    scaledSize: new google.maps.Size(18, 18),
    anchor: new google.maps.Point(9, 9),
  };
  const pinPosition = marker ?? (pinMode ? centerPosition : null);

  return (
    <GoogleMap
      mapContainerClassName={cx("w-full h-full rounded-lg", className)}
      // pinMode centres imperatively (panTo) for a smooth pan; other callers keep controlled centre.
      center={pinMode ? undefined : marker ?? centerPosition}
      zoom={zoom ?? 10}
      options={{ disableDefaultUI: true, styles: MAP_STYLES }}
      onLoad={(map) => {
        mapRef.current = map;
        if (pinMode) map.setCenter(isPlottable(center) ? center : centerPosition);
      }}
    >
      {pinMode
        ? pinPosition && (
            <Marker
              position={pinPosition}
              // All visual config in `options` (incl. crossOnDrag) so the lib honours it; optimized:false
              // renders a DOM marker where crossOnDrag is reliably respected — removes Google's "✕".
              options={{ icon: pinIcon, draggable: !!onPinChange, crossOnDrag: false, optimized: false }}
              onDragEnd={(e) => {
                if (e.latLng && onPinChange) onPinChange({ lat: e.latLng.lat(), lng: e.latLng.lng() });
              }}
            />
          )
        : marker && <Marker position={marker} />}
      {/* Booking markers (pickup dot, stops/destination pins) + route preview (independent of pinMode). */}
      {!pinMode &&
        markers
          ?.filter((m) => isPlottable(m.position))
          .map((m, i) => <Marker key={`m-${i}`} position={m.position} options={{ icon: m.variant === "dot" ? dotIcon : pinIcon }} />)}
      {!pinMode && routePath && routePath.length > 1 && (
        <Polyline path={routePath} options={{ strokeColor: ROUTE_COLOR, strokeOpacity: 1, strokeWeight: 5 }} />
      )}
      {!pinMode && routeBadge && isPlottable(routeBadge.position) && (
        <OverlayView position={routeBadge.position} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
          {/*
            The label's BOTTOM CENTRE sits on the position, so a callout with a tail points at the
            place it describes rather than covering it.

            `absolute` matters as much as the transform: OverlayView drops its child into a pane
            container with no width of its own, so an in-flow child collapses and the label wraps a
            character per line — a pill one glyph wide. Out of flow it sizes to its content.
          */}
          <div className="absolute w-max -translate-x-1/2 -translate-y-full">{routeBadge.label}</div>
        </OverlayView>
      )}
      {circleRadius && (
        <Circle
          center={centerPosition}
          radius={circleRadius}
          options={{
            strokeWeight: 0,
            fillColor: "#2E90FA",
            fillOpacity: 0.35,
            clickable: false,
            draggable: false,
            editable: false,
            visible: true,
            zIndex: 1,
          }}
        />
      )}
    </GoogleMap>
  );
});
